import React, { useMemo } from "react";
import { AccountSettingsWrapper } from "@/shared/widgets/TabNavigationWrapper/AccountSettingsWrapper";
import { useTranslation } from "react-i18next";
import { find, map, toNumber } from "lodash";
import { InputField } from "@/shared/ui/InputField";
import { Row } from "@/shared/ui/Blocks";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { useSearchAccountTalentPool } from "@/features/people/hooks/useSearchTalentPool";
import { MembersTable } from "@/shared/widgets/MembersTable";
import { FiltersDropdown } from "@/shared/widgets/FiltersDropdown";
import { AccessLevel, RoleType, Action } from "@/shared/constants";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import NoRights from "@/shared/widgets/NoRights";
import { useAccountOrganizations } from "@/features/workspace/hooks/useAccountOrganizations";
import { usePersonAccessLevelChange } from "@/features/people/hooks/usePersonAccessLevelChange";
import { usePersonRoleChange } from "@/features/people/hooks/usePersonRoleChange";
import { useRemovePersonFromWorkspace } from "@/features/workspace/hooks/useRemovePersonFromWorkspace";
import { ResourceEnum } from "@/shared/constants/resources";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

export default function () {
  const { account, loading, getAccountAssessmentSurveys } = useAccount();

  const {
    data,
    totalPages,
    limit,
    page,
    loading: loadingTable,
    sort,
    handleSearch,
    filters,
    handleFilterChange,
    clearFilter,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
  } = useSearchAccountTalentPool();

  const { organizations, loading: loadingOrganizations } =
    useAccountOrganizations(toNumber(account?.id));

  const surveyConfig = map(
    getAccountAssessmentSurveys(),
    (s) => s?.type
  ) as AssessmentSurveyType[];

  const { t } = useTranslation();
  const { handleUpdatePersonAccessLevel } = usePersonAccessLevelChange();
  const { handleUpdatePersonRole } = usePersonRoleChange();
  const { validateResourceAction, validateAccessToResource } =
    useViewerPermissions();

  const hasReadAccess = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.edit,
  });

  const hasEditAccess = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.full,
  });

  const handleSearchMember = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  const membersWithOrganizations = useMemo(
    () =>
      data.map((member) => ({
        ...member,
        organizations: organizations.filter((org) =>
          find(
            member.roles,
            (role) =>
              role?.type === ResourceEnum.organization &&
              role?.obj_id === org?.id
          )
        ),
      })),
    [data, organizations]
  );

  const handleAccessLevelChange = async (
    personId: number,
    access_level: AccessLevel
  ) => {
    return await handleUpdatePersonAccessLevel({
      personId,
      access_level,
      objectType: "account",
      objectId: account.id!,
    });
  };

  // Only reachable for workspace candidates being promoted to MEMBER — the table
  // keeps role editing disabled for every other role (see MembersTable).
  const handleRoleChange = async (personId: number, role: string) => {
    return await handleUpdatePersonRole({
      personId,
      role,
      objectType: "account",
      objectId: account.id!,
    });
  };

  const { removePersonWithConfirm } = useRemovePersonFromWorkspace();

  const emptyStateContent = {
    notice: {
      title: t("workspace.members_no_access.title"),
      subtitle: t("workspace.members_no_access.subtitle"),
    },
    thumbnail:
      "https://storage.googleapis.com/iaeqpsxmmk/onboarding_images/discovering.png",
    video: "https://storage.googleapis.com/iaeqpsxmmk/just_discovering.mp4",
    subtitle: t("workspace.members_no_access.features_subtitle"),
    list: [
      {
        title: t("workspace.members_no_access.features.hiring.title"),
        description: t(
          "workspace.members_no_access.features.hiring.description"
        ),
      },
      {
        title: t("workspace.members_no_access.features.teamDevelopment.title"),
        description: t(
          "workspace.members_no_access.features.teamDevelopment.description"
        ),
        // button: {
        //   title: "Download PDF",
        //   action: () => console.log("download pdf"),
        // },
      },
    ],
  };

  if (!hasReadAccess && !loading) {
    return (
      <ScreenContainer>
        <NoRights
          pageTitle={t("common.people")}
          emptyStateContent={emptyStateContent}
        />
      </ScreenContainer>
    );
  }

  return (
    <AccountSettingsWrapper loading={loading}>
      {/* <HeaderContent>
        <IconButton
          icon="AddUser"
          label={t("workspace.invite_admins")}
          variant="default"
          onClick={() =>
            openAddPeopleModal({
              title: t("workspace.invite_people", { name: account?.name }),
              description: t("workspace.invite_people_description"),
              // validate: "workspace",
              // role: RoleType.admin,
            })
          }
        />
      </HeaderContent> */}

      <Row className="justify-between md:items-center flex-col md:flex-row gap-4">
        <Row className="md:w-[40%]">
          <InputField
            variant="ghost"
            size="small"
            deleteIcon
            icon="Search"
            type="text"
            name="search"
            onClearValue={() => handleSearch("")}
            placeholder={t("common.search")}
            autoComplete="off"
            onChange={handleSearchMember}
            wrapperClassName="border-b-grey-300 border-b border-solid"
          />
        </Row>
        <Row className="space-x-4 justify-between md:justify-normal">
          <FiltersDropdown
            label={t("common.status")}
            items={[
              {
                id: "complete",
                label: t("common.complete"),
              },
              {
                id: "pending",
                label: t("common.surveys_pending"),
              },
              {
                id: "invited",
                label: t("common.invited"),
              },
            ]}
            selected={filters.status ? [filters.status as string] : []}
            onSelect={(value) => handleFilterChange("status", value)}
            onClear={() => clearFilter("status")}
          />
          <FiltersDropdown
            label={t("common.role")}
            items={[
              {
                id: RoleType.owner,
                label: t("common.owner"),
              },
              {
                id: RoleType.admin,
                label: t("common.admin"),
              },
              {
                id: RoleType.member,
                label: t("common.member"),
              },
              {
                id: RoleType.candidate,
                label: t("common.candidate"),
              },
              {
                id: RoleType.contact,
                label: t("common.contact"),
              },
            ]}
            selected={filters.role ? [filters.role as string] : []}
            onSelect={(value) => handleFilterChange("role", value)}
            onClear={() => clearFilter("role")}
          />
          <FiltersDropdown
            label={t("people.access_level_column")}
            items={[
              {
                id: AccessLevel.none,
                label: t("common.access_level_none"),
              },
              {
                id: AccessLevel.limited,
                label: t("common.access_level_limited"),
              },
              {
                id: AccessLevel.edit,
                label: t("common.access_level_edit"),
              },
              {
                id: AccessLevel.full,
                label: t("common.access_level_full"),
              },
            ]}
            selected={
              filters.access_level ? [filters.access_level as string] : []
            }
            onSelect={(value) => handleFilterChange("access_level", value)}
            onClear={() => clearFilter("access_level")}
          />
        </Row>
      </Row>
      <MembersTable
        className="mt-4 -mr-4 md:mr-0"
        data={membersWithOrganizations}
        pages={totalPages}
        currentPage={page}
        limit={limit}
        readOnly={!hasEditAccess}
        disableRoleChange
        onRoleChange={handleRoleChange}
        onAccessLevelChange={handleAccessLevelChange}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSort={handleSortChange}
        sort={sort}
        isFullList
        loading={loadingTable}
        tooltips={{
          access_level: t("workspace.members_access_level_tooltip"),
          role: t("workspace.members_role_tooltip"),
        }}
        removePersonWithConfirm={removePersonWithConfirm}
        resource={{
          type: ResourceEnum.account,
          id: toNumber(account?.id),
        }}
      />
    </AccountSettingsWrapper>
  );
}
